import type { Maybe } from '@adornis/base/utilTypes.js';
import { toCalendarDay, todayAsCalendarDay } from '@adornis/datetime/calendarDay.js';
import { subDays } from 'date-fns';
import { BillingRhythm } from '../../billingPositions/entities/BillingPosition.js';
import type { Booking } from '../../bookings/entities/Booking.js';

/** The day-precision runtime of a mandate. `start` and `end` are both inclusive. */
export interface MandateRuntime {
  start: Maybe<Date>;
  end: Maybe<Date>;
}

/**
 * A mandate runs on a day if that day lies within [start, end], both inclusive. A missing start
 * means it always ran, a missing end means it runs indefinitely.
 *
 * `day` must be a canonical calendar day, as must the mandate's runtime - then this is a plain
 * instant comparison. See `@adornis/datetime/calendarDay.js`.
 */
export function isMandateActiveOn(mandate: MandateRuntime, day: Date): boolean {
  const hasStarted = !mandate.start || mandate.start <= day;
  const hasNotEnded = !mandate.end || mandate.end >= day;

  return hasStarted && hasNotEnded;
}

/**
 * Whether the runtime holds no day at all: what "Mandat beenden" leaves on a mandate that has not
 * started yet (`end` the day before `start`, see {@link immediateEndDay}). Such a mandate is over
 * although its `end` may still lie ahead, so "not ended yet" alone must not count it as current.
 */
export function runsOnNoDay(runtime: Partial<MandateRuntime>): boolean {
  return !!runtime.start && !!runtime.end && runtime.end < runtime.start;
}

/** Mongo counterpart of `!runsOnNoDay`. */
export const RUNS_ON_SOME_DAY_SELECTOR = {
  $or: [{ start: null }, { end: null }, { $expr: { $gte: ['$end', '$start'] } }],
};

/**
 * Two runtimes overlap if they share at least one day. Both bounds are inclusive and both are
 * optional - a missing start reaches back indefinitely, a missing end runs on indefinitely.
 */
export function runtimesOverlap(a: MandateRuntime, b: MandateRuntime): boolean {
  if (runsOnNoDay(a) || runsOnNoDay(b)) return false;

  return (!a.start || !b.end || a.start <= b.end) && (!b.start || !a.end || b.start <= a.end);
}

/**
 * Whether the amount is dictated rather than chosen by the person accepting - a fixed price as well
 * as a dynamic one, whose amount the booking fills in.
 *
 * This is what separates a duplicate from a second, genuine mandate. Two payers may each pledge an
 * amount they pick themselves for one booking - they share the load. A dictated amount is collected
 * in full from every mandate, so splitting it between payers would make each of them overpay.
 */
export function hasFixedAmount(mandate: { canEditAmount?: Maybe<boolean> }): boolean {
  return mandate.canEditAmount !== true;
}

/** Mongo counterpart of {@link isMandateActiveOn}. */
export function getMandateActiveOnDaySelector(day: Date): Record<string, any> {
  return {
    $and: [
      { $or: [{ start: { $lte: day } }, { start: null }, { start: { $exists: false } }] },
      { $or: [{ end: { $gte: day } }, { end: null }, { end: { $exists: false } }] },
    ],
  };
}

/**
 * Whether the mandate collects nothing at all. Gefördert, Befreit and Pausiert are all stored as a
 * zero or negative amount (`BillingPositionPriceSymbols`), as is a dynamic price nobody filled in,
 * and the export skips every one of them (`SkipReasonKind.AMOUNT_ZERO`). Anything that counts the
 * SEPA mandates of a group has to skip them too - a single funded member is what made the member
 * list read 204 where the export wrote 203 lines.
 *
 * An unset amount is not a zero: such a mandate is collected with the price of its billing position.
 */
export function collectsNothing(mandate: { amountInCents?: Maybe<number> }): boolean {
  return mandate.amountInCents != null && mandate.amountInCents <= 0;
}

const monthIndex = (day: Date): number => day.getUTCFullYear() * 12 + day.getUTCMonth();
export const sameMonth = (a: Date, b: Date): boolean => monthIndex(a) === monthIndex(b);

/**
 * Whether something with `rhythm` is collected on the Stichtag, given when it was last billed
 * (`billedOn`, the Stichtag of a lock export - see {@link laterOf} for which marker counts). Only the
 * marker decides, never the group's lock: every group is exported monthly and each run collects
 * whoever is due - monthly once per month, yearly twelve months after the last collection, once
 * until it has been collected. A lock run never collects again what was billed in the Stichtag's
 * month, so a second lock run in that month (after "Freigeben") collects only what is new.
 *
 * A Vorschau (`preview`) compares the day: with a Stichtag on or before the marker it reproduces the
 * run that set it, so the files of a lock export can be produced again; with a later Stichtag it
 * shows only what is new, like a lock run.
 *
 * Never billed by a lock export, yearly follows one of two rules, and the split is deliberate: it
 * goes against the intuition that "yearly" is always anchored to the day the mandate started.
 *  - A rhythm the group fixed (`rhythmFixedByGroup`: its Abrechnungsposition says yearly, e.g.
 *    Kopiergeld) is due in the next run, exactly like monthly. Parents accept over weeks or months,
 *    and the one lock export at the end collects everybody not billed yet. From then on each one is
 *    due twelve months after that run, not after its own start.
 *  - A rhythm the payer chose (Selbstauswahl: the group leaves it open, as Elternengagement at the
 *    schools does, so monthly and yearly pledges share one group that is exported every month) is
 *    collected only in the UTC month of its valid start date (`firstBillingDay`), never without one.
 *    A yearly pledge that starts after that month's run therefore waits for the same month a year
 *    later.
 *
 * `rhythmFixedByGroup` defaults to the start-month rule, the side that collects less. A rhythm without
 * a pledge (Selbstauswahl) collects nothing.
 */
export function isDueOn(
  rhythm: Maybe<BillingRhythm>,
  stichtag: Date,
  billedOn?: Maybe<Date>,
  firstBillingDay?: Maybe<Date>,
  preview = false,
  rhythmFixedByGroup = false,
): boolean {
  // a Vorschau on or before the collecting run sees the mandate as that run did
  const reproduces = preview && !!billedOn && stichtag <= billedOn;

  switch (rhythm) {
    case BillingRhythm.MONTHLY:
      return !billedOn || reproduces || monthIndex(stichtag) > monthIndex(billedOn);
    case BillingRhythm.YEARLY:
      if (!billedOn)
        return (
          rhythmFixedByGroup ||
          (firstBillingDay instanceof Date &&
            !Number.isNaN(firstBillingDay.getTime()) &&
            firstBillingDay.getUTCMonth() === stichtag.getUTCMonth())
        );
      // only the month of the marker: whether an earlier month was due went with the previous marker
      return (reproduces && sameMonth(billedOn, stichtag)) || monthIndex(stichtag) - monthIndex(billedOn) >= 12;
    case BillingRhythm.ONCE_CUSTOM:
      return !billedOn || reproduces;
    default:
      return false;
  }
}

/** The first day of the month in which something yearly billed on `billedOn` is due again. */
export function getYearlyDueAgainDay(billedOn: Date): Date {
  return new Date(Date.UTC(billedOn.getUTCFullYear() + 1, billedOn.getUTCMonth(), 1));
}

/**
 * The later of two collection markers. A mandate is measured against its own `billedOn` and the
 * booking's (a Soll the booking got before the mandate was accepted counts); a Selbstzahler booking
 * against its own and the last collection of any of its mandates (a mandate collected and then
 * withdrawn counts).
 */
export function laterOf(a: Maybe<Date>, b: Maybe<Date>): Maybe<Date> {
  if (!a) return b;
  if (!b) return a;

  return a > b ? a : b;
}

/** Whether two runtimes hold exactly the same days. */
export function isSameRuntime(a: MandateRuntime, b: MandateRuntime): boolean {
  return a.start?.getTime() === b.start?.getTime() && a.end?.getTime() === b.end?.getTime();
}

/**
 * The runtime a mandate may have under its booking. A mandate with a {@link hasFixedAmount | dictated
 * amount} bills that booking, so it never runs on a day the booking does not: it starts on or after
 * the booking, ends on or before it, and stays open-ended only while the booking does. Ending a
 * booking therefore ends its mandates with it. A running mandate left behind on an ended booking is
 * collected by nothing - the export reads mandates through the bookings active on its Stichtag - and
 * shows up in no Hinweis either; a duplicate booking ended under its mandate lost a Lastschrift that
 * way. A booking that ends before the mandate starts leaves the runtime of no day, the one "Mandat
 * beenden" leaves on a planned mandate ({@link runsOnNoDay}).
 *
 * A pledge (Elternengagement) keeps its runtime: it follows the child from one schoolyear's class
 * booking to the next (`rewriteDonationSepaTpActiveBookingJob`).
 */
export function clampToBookingRuntime(
  mandate: MandateRuntime & { canEditAmount?: Maybe<boolean> },
  booking: Pick<Booking, 'startDate' | 'endDate'>,
): MandateRuntime {
  if (!hasFixedAmount(mandate)) return { start: mandate.start, end: mandate.end };

  const bookingEnd = toCalendarDay(booking.endDate);
  // a missing date is no bound: the later of the starts, the earlier of the ends
  const start = laterOf(mandate.start, toCalendarDay(booking.startDate));
  const end = !mandate.end || (bookingEnd && bookingEnd < mandate.end) ? bookingEnd : mandate.end;
  if (!start || !end || end >= start) return { start, end };

  // subDays counts on the local clock, which is an hour off across a DST change in the browser
  return { start, end: toCalendarDay(subDays(start, 1)) };
}

/**
 * The day an "end it now" action sets. `end` is inclusive, so ending a mandate *yesterday* takes it
 * out of an export run with today's Stichtag as well. Every "end it now" uses it - the card and
 * `endMandatesNow` on the server.
 *
 * A mandate that has not started yet ends on the day before its `start` instead: it never runs
 * either way, and yesterday would be a {@link isNegativeRuntime | negative runtime}.
 */
export function immediateEndDay(start?: Maybe<Date>): Date {
  return getMandateEndDay(subDays(todayAsCalendarDay(), 1), start);
}

/**
 * The end a mandate gets when it is ended on `lastDay`: that day, or the day before its `start` when
 * it only starts later - it then runs on no day ({@link runsOnNoDay}) instead of a negative runtime.
 * What {@link clampToBookingRuntime} leaves on a mandate whose booking ends on `lastDay`.
 */
export function getMandateEndDay(lastDay: Date, start?: Maybe<Date>): Date {
  // subDays counts on the local clock, which is an hour off across a DST change in the browser
  const dayBeforeStart = start ? toCalendarDay(subDays(start, 1)) : lastDay;

  return dayBeforeStart > lastDay ? dayBeforeStart : lastDay;
}

/**
 * Whether the runtime ends before it could have started. Both bounds are inclusive, so an `end` on
 * the day before `start` is the runtime of no days - what {@link immediateEndDay} leaves on a mandate
 * accepted the same day, which today's export must not collect any more. Anything earlier means
 * nothing a mandate could say: a typo in the year, start and end swapped.
 *
 * A runtime that is already stored like that never counts (`previous`), the same way an unchanged
 * end is no {@link isBackdatedEnd | backdating}: the owner must still be able to change the IBAN of
 * a mandate somebody else gave impossible dates.
 */
export function isNegativeRuntime(runtime: MandateRuntime, previous?: Maybe<MandateRuntime>): boolean {
  const { start, end } = runtime;
  if (!start || !end || end >= subDays(start, 1)) return false;

  return start.getTime() !== previous?.start?.getTime() || end.getTime() !== previous?.end?.getTime();
}

/**
 * Whether the person who accepted the stored mandate may still move its `start` outside an
 * acceptance (`isAcceptance` - taking an ended row over picks a fresh start). Only while nothing was
 * collected under it (`billedOn`) and it has not started yet - otherwise an owner could push the
 * start past a collection that already happened and dispute it. A mandate without a start always
 * ran, so it is locked as well.
 *
 * The administrative SEPA roles are not bound by this.
 */
export function isStartMovableByOwner(
  stored: Partial<MandateRuntime> & { accepted?: Maybe<boolean>; billedOn?: Maybe<Date> },
): boolean {
  if (!stored.accepted) return true;
  if (stored.billedOn || !stored.start) return false;

  return stored.start > todayAsCalendarDay();
}

/**
 * Whether setting a mandate's start to `start` backdates it: consent cannot reach back before the
 * day it is given. An unchanged value never counts, like {@link isBackdatedEnd}.
 */
export function isBackdatedStart(start: Maybe<Date>, previousStart: Maybe<Date>): boolean {
  if (!start) return false;
  if (previousStart && start.getTime() === previousStart.getTime()) return false;

  return start < todayAsCalendarDay();
}

/**
 * Whether setting a mandate's end to `end` backdates it. Ending a mandate can only mean "stop it
 * now" ({@link immediateEndDay}) or "stop it on a future day"; anything earlier rewrites a runtime
 * that has already been exported and billed. Sorgeberechtigte may only end their own mandates, so
 * for them that would be a way to make collections that already happened look unauthorised -
 * backdating is therefore reserved for the administrative SEPA roles.
 *
 * An unchanged value never counts as backdating, so an owner can still edit the other fields of a
 * mandate an administrator ended in the past.
 *
 * Enforced by the `end` form validator in `SepaMandateCard` only - `saveMandat` and the generic
 * `updateEntity` both still accept any end date, so this is a guard rail, not a permission.
 */
export function isBackdatedEnd(end: Maybe<Date>, previousEnd: Maybe<Date>): boolean {
  if (!end) return false;
  if (previousEnd && end.getTime() === previousEnd.getTime()) return false;

  return end < immediateEndDay();
}
